import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowUpRight, Radio, MessageCircle } from "lucide-react";
import { TID } from "@/lib/testIds";
import { useLinks } from "@/admin/SiteContent";
import BrandLogo from "@/components/BrandLogo";

const NAV_ITEMS = [
  { label: "Home", href: "#top", tid: TID.nav.linkServer },
  { label: "Lore", href: "#lore", tid: TID.nav.linkFeatures },
  { label: "Universo", href: "#universo", tid: "nav-link-universo" },
  { label: "Forze dell'Ordine", href: "#factions", tid: TID.nav.linkFactions },
  { label: "Galleria", href: "#gallery", tid: TID.nav.linkGallery },
  { label: "Regolamento", href: "/regolamento", tid: TID.nav.linkFaq, page: true },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const LINKS = useLinks();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Body scroll lock when drawer is open */
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  /* Close on Escape */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header
      data-testid={TID.nav.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center gap-4">
        {/* Server status pill (left) — desktop only */}
        <div
          data-testid={TID.nav.serverStatus}
          className="hidden lg:flex status-pill shrink-0"
        >
          <Radio size={12} />
          <span>Server Offline</span>
        </div>

        {/* Main nav (center) */}
        <nav
          className={`relative flex-1 flex items-center justify-between rounded-full pl-4 pr-2 py-2 ${
            scrolled ? "glass-strong" : "glass"
          }`}
        >
          <a
            href="#top"
            data-testid={TID.nav.logo}
            className="flex items-center gap-3 group pl-2"
            aria-label="PURELIFE Roleplay"
          >
            <BrandLogo size="xs" showSub={false} className="!flex-row !items-center gap-2" />
          </a>

          <ul className="hidden md:flex items-center gap-7 text-sm">
            {NAV_ITEMS.map((it) => (
              <li key={it.label}>
                {it.page ? (
                  <Link
                    to={it.href}
                    data-testid={it.tid}
                    className="text-[#B6B6BE] hover:text-[#F5F5F5] font-medium transition-colors duration-300 relative group"
                  >
                    {it.label}
                    <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-[#00D4FF] transition-all duration-500 group-hover:w-full" />
                  </Link>
                ) : (
                  <a
                    href={it.href}
                    data-testid={it.tid}
                    className="text-[#B6B6BE] hover:text-[#F5F5F5] font-medium transition-colors duration-300 relative group"
                  >
                    {it.label}
                    <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-[#00D4FF] transition-all duration-500 group-hover:w-full" />
                  </a>
                )}
              </li>
            ))}
          </ul>

          <a
            data-testid={TID.nav.join}
            href={LINKS.join}
            className="hidden sm:inline-flex btn-gold rounded-full px-5 py-2.5 text-xs sm:text-sm font-bold tracking-wide items-center gap-1.5"
          >
            ENTRA ORA
            <ArrowUpRight size={14} strokeWidth={2.6} />
          </a>

          {/* Mobile toggle — animated icon morph */}
          <button
            data-testid={TID.nav.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            className={`md:hidden relative inline-flex items-center justify-center w-10 h-10 ml-2 rounded-full border transition-all duration-300 ${
              open
                ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37] shadow-[0_0_18px_-4px_rgba(212,175,55,0.7)]"
                : "border-white/10 text-[#F5F5F5] hover:border-white/25"
            }`}
          >
            <span className="relative w-[18px] h-[18px]">
              <Menu
                size={18}
                strokeWidth={2.2}
                className={`absolute inset-0 transition-all duration-300 ${
                  open
                    ? "opacity-0 rotate-90 scale-75"
                    : "opacity-100 rotate-0 scale-100"
                }`}
              />
              <X
                size={18}
                strokeWidth={2.4}
                className={`absolute inset-0 transition-all duration-300 ${
                  open
                    ? "opacity-100 rotate-0 scale-100"
                    : "opacity-0 -rotate-90 scale-75"
                }`}
              />
            </span>
          </button>
        </nav>

        {/* Discord round (right) */}
        <a
          data-testid={TID.nav.discord}
          href={LINKS.discord}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden lg:inline-flex shrink-0 items-center justify-center w-11 h-11 rounded-full glass border border-white/10 hover:border-[#00D4FF]/60 transition-colors"
          aria-label="Discord"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#F5F5F5">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03ZM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418Zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418Z" />
          </svg>
        </a>
      </div>

      {/* Mobile drawer — backdrop + slide-in panel */}
      <div
        aria-hidden={!open}
        className={`md:hidden fixed inset-0 z-[60] ${
          open ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 transition-opacity duration-500 ${
            open ? "opacity-100" : "opacity-0"
          }`}
          style={{
            background:
              "radial-gradient(120% 90% at 100% 0%, rgba(212,175,55,0.18), transparent 55%), rgba(8, 8, 12, 0.78)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        />

        {/* Panel */}
        <aside
          className={`drawer absolute top-0 right-0 h-full w-[86vw] max-w-[360px] flex flex-col ${
            open ? "is-open" : ""
          }`}
          style={{
            background:
              "linear-gradient(180deg, rgba(20,20,27,0.96), rgba(11,11,15,0.98))",
            borderLeft: "1px solid rgba(212, 175, 55, 0.22)",
            boxShadow:
              "-30px 0 80px -20px rgba(0,0,0,0.7), inset 1px 0 0 rgba(255,255,255,0.04)",
          }}
        >
          {/* Decorative golden seam */}
          <div
            className="absolute top-0 left-0 bottom-0 w-px pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, transparent, rgba(212,175,55,0.7) 30%, rgba(0,212,255,0.4) 70%, transparent)",
              boxShadow: "0 0 18px rgba(212,175,55,0.5)",
            }}
          />

          {/* Header inside drawer */}
          <div className="drawer-item flex items-center justify-between px-6 pt-7 pb-6 border-b border-white/[0.06]">
            <BrandLogo size="xs" showSub={false} className="!flex-row !items-center gap-2" />
            <button
              onClick={() => setOpen(false)}
              aria-label="Chiudi"
              className="w-9 h-9 rounded-full border border-white/10 text-[#B6B6BE] hover:text-[#F5F5F5] hover:border-white/25 transition-colors inline-flex items-center justify-center"
            >
              <X size={16} strokeWidth={2.4} />
            </button>
          </div>

          {/* Server status mini */}
          <div className="drawer-item px-6 pt-5 pb-3">
            <div className="status-pill w-fit">
              <Radio size={11} />
              <span>Server Offline</span>
            </div>
          </div>

          {/* Nav links */}
          <nav className="px-3 pt-2 pb-5 flex flex-col gap-1">
            {NAV_ITEMS.map((it, i) => {
              const Tag = it.page ? Link : "a";
              const linkProps = it.page ? { to: it.href } : { href: it.href };
              return (
                <Tag
                  key={it.label}
                  {...linkProps}
                  onClick={() => setOpen(false)}
                  className="drawer-item group flex items-center justify-between px-4 py-3.5 rounded-xl text-[#F5F5F5] hover:bg-white/[0.04] transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <span className="font-mono-pl text-[10px] text-[#D4AF37]/70 tracking-[0.25em]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-base font-semibold tracking-wide">
                      {it.label}
                    </span>
                  </span>
                  <ArrowUpRight
                    size={14}
                    className="text-[#B6B6BE]/60 group-hover:text-[#D4AF37] -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                    strokeWidth={2.4}
                  />
                </Tag>
              );
            })}
          </nav>

          {/* Bottom CTAs */}
          <div className="drawer-item mt-auto px-6 pb-7 pt-5 border-t border-white/[0.06] flex flex-col gap-3">
            <a
              href={LINKS.join}
              data-testid="mobile-drawer-join"
              className="btn-gold rounded-full px-5 py-3.5 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
            >
              Entra ora su PureLife
              <ArrowUpRight size={15} strokeWidth={2.6} />
            </a>
            <a
              href={LINKS.discord}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-drawer-discord"
              className="btn-ghost-neon rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={14} strokeWidth={2.4} />
              Unisciti al Discord
            </a>
            <p className="text-[10px] text-center text-[#B6B6BE]/55 uppercase tracking-[0.28em] mt-1">
              MMXXVI · Codice della Città
            </p>
          </div>
        </aside>
      </div>
    </header>
  );
};

export default Navbar;
